/**
 * Sticky site header: logo, primary nav (NavMenu), and — when logged in — account actions.
 * Used for public pages and for authenticated readers (full-width /app shell).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaSearch } from "react-icons/fa";
import BrandLogo from "../siteHeader/BrandLogo";
import NavMenu from "../siteHeader/NavMenu";
import LogoutModal from "../siteHeader/LogoutModal";
import { logoutThunk, selectUser } from "../../auth";
import { PATHS } from "../../config/routePaths";

export default function PublicSiteHeader() {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const navRef = useRef(null);
  const closeNavbar = useCallback(() => setNavbarOpen(false), []);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [headerQuery, setHeaderQuery] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarOpen && navRef.current && !navRef.current.contains(event.target)) {
        setNavbarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [navbarOpen]);

  const submitHeaderSearch = (event) => {
    event.preventDefault();
    const q = headerQuery.trim();
    closeNavbar();
    navigate(q ? `${PATHS.SEARCH}?q=${encodeURIComponent(q)}` : PATHS.SEARCH);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await dispatch(logoutThunk()).unwrap();
      setShowLogoutModal(false);
      closeNavbar();
      navigate(PATHS.HOME);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <nav
      ref={navRef}
      className="navbar navbar-expand-xl navbar-light px-3 px-md-4 sticky-top public-site-header topbar-surface"
    >
      <div className="container-fluid px-2 px-md-3">
        <div className="d-flex align-items-center gap-2 flex-wrap flex-xl-nowrap w-100">
          <div className="flex-shrink-0">
            <BrandLogo to={PATHS.HOME} />
          </div>
          <button
            className="navbar-toggler ms-auto border-0 d-xl-none"
            type="button"
            onClick={() => setNavbarOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={navbarOpen}
            style={{ minWidth: "44px", minHeight: "44px", padding: "8px" }}
          >
            <span className="navbar-toggler-icon" />
          </button>
          <div className={`collapse navbar-collapse flex-xl-grow-1 ms-xl-auto ${navbarOpen ? "show" : ""}`}>
            <div
              className={`d-flex w-100 flex-column flex-xl-row flex-xl-nowrap align-items-stretch align-items-xl-center mt-3 mt-xl-0 gap-3 gap-xl-2 topbar-collapse-panel ${
                navbarOpen ? "is-open" : ""
              }`}
            >
              <div className="d-flex align-items-center flex-grow-1 position-relative topbar-nav-scroll-wrap gap-2">
                <div className="flex-shrink-0">
                  <NavMenu onRouteClick={closeNavbar} showIcons={false} />
                </div>
                <form className="platform-nav-search" role="search" onSubmit={submitHeaderSearch}>
                  <FaSearch aria-hidden />
                  <label className="visually-hidden" htmlFor="platform-nav-search">Search Swarand</label>
                  <input
                    id="platform-nav-search"
                    type="search"
                    value={headerQuery}
                    onChange={(event) => setHeaderQuery(event.target.value)}
                    placeholder="Search titles, writers, genres"
                    autoComplete="off"
                  />
                </form>
              </div>
              {user?.uid ? (
                <div className="d-flex flex-shrink-0 align-items-center gap-2 ms-xl-auto pt-2 pt-xl-0 border-top border-xl-0 border-secondary border-opacity-25">
                  <button
                    type="button"
                    className="platform-nav-text"
                    onClick={() => {
                      setShowLogoutModal(true);
                      closeNavbar();
                    }}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="d-flex flex-shrink-0 align-items-center gap-2 ms-xl-auto pt-2 pt-xl-0">
                  <Link to={PATHS.browse()} className="btn btn-primary platform-nav-cta" onClick={closeNavbar}>Start reading</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showLogoutModal && createPortal(
        <LogoutModal
          show={showLogoutModal}
          onConfirm={handleLogout}
          onCancel={() => {
            if (!loggingOut) setShowLogoutModal(false);
          }}
          loading={loggingOut}
        />,
        document.body
      )}
    </nav>
  );
}
